import React, { useRef, useState, useEffect } from 'react';
import {
  get,
  map,
  omit,
  isEmpty,
  includes,
  reduce,
  intersection,
  flatten,
  getOr,
  flow,
  values
} from 'lodash/fp';

import {
  Form,
  Schema,
  FormControl,
  FormGroup,
  ControlLabel,
  CheckPicker,
  Button,
  ButtonToolbar,
  RadioGroup,
  Radio,
  Icon,
  Tooltip,
  Whisper,
  DateRangePicker,
  Panel
} from 'rsuite';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInventoryLocationOptions,
  fetchTrackingMethodOptions,
  fetchGuestSummaryReport
} from '../../../../redux/Inventory/sagas/routines';
import { getIsFetching } from '../../../../redux/Inventory/selectors/reports/guest-summary';
import {
  getLocationOptions,
  getIsFetchingLocationOptions
} from '../../../../redux/Inventory/selectors/components/adjust-item-form';
import {
  getActiveTrackingMethodsOptions,
  getIsFetchingTrackingMethodsOptions
} from '../../../../redux/Inventory/selectors/components/search-options';
import {
  resetGuestSummaryReport,
  updateGuestSummaryReportSearchQuery
} from '../../../../redux/Inventory/actions/reports/guest-summary';
import {
  InlineHelpBlock,
  InlineFormGroup
} from '../../../widgets/FormElements';
import {
  DateRangeOptions,
  GroupByOptions,
  ColumnOptionsForGroupBy
} from './form-options';
import ReportTemplatesModal from '../../../../containers/ReportTemplatesModal';
import { GUEST_SUMMARY } from '../../../../helpers/ReportTemplateTypes';
import { toStandardDate } from '../../../../helpers/dates';

const StyledPanel = styled(Panel)`
  > .rs-panel-body {
    padding-bottom: 8px;
  }
`;

const StyledFormGroup = styled(InlineFormGroup)`
  margin-bottom: 0 !important;
`;

const ColumnsCheckPicker = styled(CheckPicker)`
  label! > .sub-menu-item {
    background: red;
  }
`;

const { StringType, ArrayType } = Schema.Types;

const VISIT_ON = 'visit_on';
const CREATED_AT = 'createdAt';

const columnOptions = reduce(
  (all, each) => [
    ...all,
    each,
    ...getOr([], each.value, ColumnOptionsForGroupBy)
  ],
  [],
  GroupByOptions
);

const groupByValues = map('value', GroupByOptions);
const columnValues = flow(
  values,
  flatten,
  map('value')
)(ColumnOptionsForGroupBy);

export default () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [dateRangeField, setDateRangeField] = useState(CREATED_AT);
  const [showPresetModal, setShowPresetModal] = useState(false);

  const isFetching = useSelector(getIsFetching);
  const locationOptions = useSelector(getLocationOptions);
  const isLoadingLocations = useSelector(getIsFetchingLocationOptions);
  const trackingMethodOptions = useSelector(getActiveTrackingMethodsOptions);
  const isLoadingTrackingMethods = useSelector(
    getIsFetchingTrackingMethodsOptions
  );

  const revalidateForm = () => {
    setTimeout(formRef.current.check, 1);
  };

  const setDefaultFormValue = () => {
    const dateRange = get('0.value', DateRangeOptions);
    const inventoryLocationId = map('value', locationOptions);
    const trackingMethodId = map('value', trackingMethodOptions);
    setFormValue({
      [dateRangeField]: dateRange,
      inventoryLocationId,
      trackingMethodId
    });
  };

  useEffect(() => {
    dispatch(fetchInventoryLocationOptions());
    dispatch(fetchTrackingMethodOptions());
  }, []);

  useEffect(
    () => {
      if (!isEmpty(locationOptions) && !isEmpty(trackingMethodOptions)) {
        setDefaultFormValue();
      }
    },
    [locationOptions, trackingMethodOptions]
  );

  const groupBy = get('groupBy', formValue);
  useEffect(
    () => {
      if (includes('householdTotal', groupBy) && !includes('clientId', groupBy))
        setFormValue({ ...formValue, groupBy: [...groupBy, 'clientId'] });
    },
    [groupBy]
  );

  const executeSearch = query => {
    const dateRange = map(toStandardDate, query[dateRangeField]);
    const searchQuery = {
      ...query,
      [dateRangeField]: dateRange,
      groupBy: intersection(query.groupBy, groupByValues),
      columns: intersection(query.groupBy, columnValues)
    };
    dispatch(fetchGuestSummaryReport(searchQuery));
    dispatch(updateGuestSummaryReportSearchQuery(searchQuery));
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = error => {
    setFormError(error);
  };

  const handleFormSubmit = () => {
    const noError = formRef.current.check();
    if (noError) {
      executeSearch(formValue);
    }
  };

  const handleFormReset = () => {
    setDefaultFormValue();
    revalidateForm();
    dispatch(resetGuestSummaryReport());
  };

  const onHidePresetModal = () => {
    setShowPresetModal(false);
  };

  const handleTogglePresetModal = () => {
    const showModal = !showPresetModal;
    setShowPresetModal(showModal);
  };

  const handleSelectTemplate = ({ file }) => {
    onHidePresetModal();
    const dateRange = get(dateRangeField, formValue);
    const searchQuery = { ...file, [dateRangeField]: dateRange };
    setFormValue(searchQuery);
    revalidateForm();

    if (!isEmpty(dateRange)) {
      executeSearch(searchQuery);
    }
  };

  const handleChangeDateRangeOption = selectedField => {
    const dateRangeValue = get(dateRangeField, formValue);
    setFormValue({
      ...omit(dateRangeField, formValue),
      [selectedField]: dateRangeValue
    });
    setDateRangeField(selectedField);
  };

  const disabledAction =
    isFetching || isLoadingLocations || isLoadingTrackingMethods;

  const formModel = Schema.Model({
    [dateRangeField]: ArrayType().isRequired('This field is required'),
    inventoryLocationId: ArrayType()
      .isRequired('This field is required')
      .minLength(1, 'Please select at least 1.'),
    trackingMethodId: ArrayType()
      .isRequired('This field is required')
      .minLength(1, 'Please select at least 1.'),
    groupBy: ArrayType().of(StringType())
  });

  return (
    <StyledPanel bordered>
      <Form
        ref={formRef}
        model={formModel}
        formValue={formValue}
        onChange={handleFormChange}
        onCheck={handleFormCheck}
      >
        <StyledFormGroup>
          <ControlLabel>
            Select Date From - To*
            <InlineHelpBlock tooltip>
              Select the date range you are reporting for and click
              &quot;OK&quot;
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 230 }}
            placeholder="Select date range"
            name={dateRangeField}
            accepter={DateRangePicker}
            ranges={DateRangeOptions}
            errorMessage={get(dateRangeField, formError)}
          />
          <FormGroup>
            <RadioGroup
              inline
              name="dateRange"
              defaultValue={dateRangeField}
              onChange={handleChangeDateRangeOption}
            >
              <Radio value={CREATED_AT}>Log Date</Radio>
              <Radio value={VISIT_ON}>Visit Date</Radio>
            </RadioGroup>
          </FormGroup>
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Inventory Locations*
            <InlineHelpBlock tooltip>
              Select one or multiple locations.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder={
              isLoadingLocations ? 'Loading...' : 'Inventory Locations'
            }
            name="inventoryLocationId"
            accepter={CheckPicker}
            data={locationOptions}
            errorMessage={formError.inventoryLocationId}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Tracking Methods*
            <InlineHelpBlock tooltip>
              Select the Tracking Methods you are reporting on.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder={
              isLoadingTrackingMethods ? 'Loading...' : 'Tracking Methods'
            }
            name="trackingMethodId"
            accepter={CheckPicker}
            data={trackingMethodOptions}
            errorMessage={formError.trackingMethodId}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Columns
            <InlineHelpBlock tooltip>
              Select the type of Columns you want to run.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 170 }}
            placeholder="Columns"
            name="groupBy"
            accepter={ColumnsCheckPicker}
            data={columnOptions}
            errorMessage={formError.groupBy}
          />
        </StyledFormGroup>
        <ButtonToolbar style={{ float: 'right', marginTop: 24 }}>
          <Button
            appearance="primary"
            onClick={handleFormSubmit}
            disabled={disabledAction}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            appearance="default"
            onClick={handleFormReset}
            disabled={disabledAction}
          >
            <Icon icon="refresh" /> Reset
          </Button>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>View Presets</Tooltip>}
          >
            <Button
              appearance="primary"
              color="green"
              onClick={handleTogglePresetModal}
              disabled={disabledAction}
            >
              <Icon icon="list" /> Presets
            </Button>
          </Whisper>
        </ButtonToolbar>
      </Form>
      <ReportTemplatesModal
        show={showPresetModal}
        onHide={onHidePresetModal}
        onSelect={handleSelectTemplate}
        fileType={GUEST_SUMMARY}
      />
    </StyledPanel>
  );
};
